import {
  AdvancedList,
  AdvancedListItem,
  EntityRef,
  SandboxFileKeyFileName,
} from "@amzn/altar-sds-client";
import {
  APIOutput,
  ContainerRef,
  FieldConfiguration,
  IAdvancedList,
  PageFactory
} from "@amzn/ask-legal-domain";
import {
  Box,
  Flashbar,
  FlashbarProps,
  Icon,
  Popover,
  SpaceBetween,
  Spinner,
  SpinnerProps,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import * as React from "react";
import { AppContext } from "../../setup/context";
import {
  CreateAdvancedListItemCommandInputEdit,
  CreateAdvancedListItemCommandInputEditProps
} from "./CreateAdvancedListItemCommandInputEdit";
import { Builder } from "builder-pattern";
import { SDSUtils } from "../../utils/sds-utils";
import { AttachmentUploadV2 } from "./attachment-components/AttachmentUpload";
import { ConfirmLeavePageModal } from "../common/ConfirmLeavePageModal";
import { SimpleModal } from "../common/SimpleModal";
import { AppURLFactory } from "../../factory/app-url-factory";

export interface CreateAdvancedListItemModalProps {
  listRef: EntityRef;
  advancedList: AdvancedList;
  containerRef: ContainerRef;
  fieldConfigurations?: FieldConfiguration.Record;
  onCreated?: (item: AdvancedListItem) => void;
  onCanceled?: () => void;
}

export function CreateAdvancedListItemModal(props: CreateAdvancedListItemModalProps) {
  const context = React.useContext(AppContext);
  const [spinnerProps, setSpinnerProps] = React.useState<SpinnerProps>();
  const [attachmentFiles, setAttachmentFiles] = React.useState<SandboxFileKeyFileName[]>([]);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);
  const [flashbarProps, setFlashbarProps] =
    React.useState<Pick<FlashbarProps, "items">>();
  const [
    createAdvancedListItemCommandInputEditProps,
    setCreateAdvancedListItemCommandInputEditProps
  ] = React.useState<CreateAdvancedListItemCommandInputEditProps>({
    listRef: props.listRef,
    advancedList: Builder(props.advancedList)
      .fieldDefinitions(props.advancedList.fieldDefinitions
      .filter((def) => !def.deprecated))
      .build(),
    fieldConfigurations: props.fieldConfigurations,
  });

  async function createItem() {
    try {
      setSpinnerProps({});

      const createAdvancedListItemInput: IAdvancedList.CreateAdvancedListItemInput = {
        ...createAdvancedListItemCommandInputEditProps.value,
        by: SDSUtils.getAmazonPersonRef(context.userId),
        containerId: props.containerRef.id,
        attachments: attachmentFiles.length > 0 ? attachmentFiles : undefined
      };
      const createItemOutput = await context.getAdvancedListAPI().createItem(createAdvancedListItemInput);

      const output = APIOutput.fromRaw<IAdvancedList.CreateAdvancedListItemOutput>(createItemOutput.data);
      if (output.isErr()) {
        setFlashbarProps({
          items: [
            {
              type: "error",
              content: output.err.message
            }
          ]
        });
      } else {
        setFlashbarProps({
          items: []
        });
        props.onCreated?.(output.data.body);
      }
    } catch (err) {
      setFlashbarProps({
        items: [
          {
            type: "error",
            content: (err as Error).message
          }
        ]
      });
    } finally {
      setSpinnerProps(undefined);
    }
  }

  const getCreateAdvancedListItemLink = () => {
    if (!props.containerRef) return "";
    const queryParams = new Map<string, string>();
    queryParams.set("container", props.containerRef.id);
    queryParams.set("action", "create");
    return AppURLFactory.getPage(PageFactory.fromEntityId(props.containerRef.id), {
      subPath: "live",
      queryParams
    });
  };

  return (
    <>
      <ConfirmLeavePageModal showModal={true} />
      <SimpleModal
        header={
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <span>Create New Item</span>
            <Popover
              size="small"
              position="top"
              dismissButton={false}
              triggerType="custom"
              content={<StatusIndicator type="success">Create Item Link Copied</StatusIndicator>}
            >
              <span
                style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", paddingLeft: 4 }}
                onClick={() =>
                  navigator.clipboard.writeText(
                    window.location.origin + getCreateAdvancedListItemLink()
                  )
                }
                title="Copy Create Item Link"
              >
                <Icon name="anchor-link" variant="subtle" />
              </span>
            </Popover>
          </SpaceBetween>
        }
        child={
          <SpaceBetween size="m">
            {spinnerProps && (
              <Box textAlign="center">
                <SpaceBetween size="s" direction="horizontal">
                  <Spinner />
                  <span>Loading...</span>
                </SpaceBetween>
              </Box>
            )}
            {(flashbarProps?.items.length ?? 0) > 0 && <Flashbar {...flashbarProps} />}
            {createAdvancedListItemCommandInputEditProps && (
              <SpaceBetween size="m">
                <CreateAdvancedListItemCommandInputEdit
                  {...createAdvancedListItemCommandInputEditProps}
                  onChanged={(value) => {
                    setCreateAdvancedListItemCommandInputEditProps((prev) => ({
                      ...prev,
                      value
                    }));
                  }}
                />
                <hr />
                <AttachmentUploadV2
                  instanceId={props.listRef.repositoryRef.repositoryId!}
                  onUpdated={(files) => setAttachmentFiles(files)}
                  setUploadingSandboxFiles={setUploadingFiles}
                />
              </SpaceBetween>
            )}
          </SpaceBetween>
        }
        loading={!!spinnerProps || uploadingFiles}
        disabled={uploadingFiles}
        onCancel={props.onCanceled}
        onSave={createItem}
        size="large"
      />
    </>
  );
}
